import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import config from '../config';
import LoadingSpinner from './LoadingSpinner';

// Load Stripe outside of component to avoid recreating on every render
// Use environment variable for publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_test_key_here');

const PaymentFormContent = ({ 
  paymentType, 
  amount, 
  description, 
  onSuccess, 
  onError, 
  onCancel,
  userId,
  leagueId 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment intent
      const response = await fetch(`${config.API_URL}/payment/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          payment_type: paymentType,
          user_id: userId,
          league_id: leagueId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create payment intent');
      }

      const { client_secret } = await response.json();

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        onError && onError(stripeError.message);
      } else if (paymentIntent.status === 'succeeded') {
        // Confirm payment with backend
        const confirmResponse = await fetch(`${config.API_URL}/payment/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            payment_intent_id: paymentIntent.id
          }),
        });

        if (confirmResponse.ok) {
          onSuccess && onSuccess(paymentIntent);
        } else {
          throw new Error('Failed to confirm payment');
        }
      }
    } catch (err) {
      setError(err.message);
      onError && onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#2563eb', marginBottom: 8 }}>Payment Details</h3>
        <div style={{ 
          background: '#f8fafc', 
          padding: 16, 
          borderRadius: 8, 
          marginBottom: 16 
        }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Amount:</strong> ${(amount / 100).toFixed(2)}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Description:</strong> {description}
          </div>
        </div>
        <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: 0 }}>
          This is a one-time platform fee for access to Fantasy Football Classic™ features—not a wager or prize entry.
          By paying, you agree to our{' '}
          <a href="/terms" style={{ color: '#2563eb' }}>Terms of Service</a>
          {' '}and{' '}
          <a href="/terms#refund-policy" style={{ color: '#2563eb' }}>Refund Policy</a>.
        </p>
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={{ 
          display: 'block', 
          marginBottom: 8, 
          fontWeight: 600, 
          color: '#374151' 
        }}>
          Card Information
        </label>
        <div style={{ 
          border: '1px solid #d1d5db', 
          borderRadius: 8, 
          padding: 12,
          background: '#fff'
        }}>
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div style={{ 
          color: '#dc2626', 
          background: '#fef2f2', 
          padding: 12, 
          borderRadius: 6, 
          marginBottom: 16,
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          type="submit"
          disabled={!stripe || loading}
          style={{
            flex: 1,
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 6,
            fontSize: 16,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <LoadingSpinner size="small" color="#fff" text="" />
              Processing...
            </div>
          ) : (
            `Pay $${(amount / 100).toFixed(2)}`
          )}
        </button>
        
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: '#6b7280',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 6,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const PaymentForm = ({ 
  paymentType, 
  onSuccess, 
  onError, 
  onCancel,
  userId,
  leagueId 
}) => {
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentConfig = async () => {
      try {
        const response = await fetch(`${config.API_URL}/payment/config`);
        if (response.ok) {
          const config = await response.json();
          setPaymentConfig(config);
        } else {
          throw new Error('Failed to load payment configuration');
        }
      } catch (error) {
        console.error('Error loading payment config:', error);
        onError && onError('Failed to load payment configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentConfig();
  }, [onError]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <LoadingSpinner text="Loading payment form..." />
      </div>
    );
  }

  if (!paymentConfig) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>
        Failed to load payment configuration
      </div>
    );
  }

  const pricing = paymentConfig.pricing[paymentType];
  if (!pricing) {
    const freeMessage =
      paymentType === 'public_league' && paymentConfig.public_leagues_free
        ? 'Public leagues are free — use Quick Join on the league page.'
        : 'Invalid payment type';
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
        {freeMessage}
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent
        paymentType={paymentType}
        amount={pricing.amount}
        description={pricing.description}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
        userId={userId}
        leagueId={leagueId}
      />
    </Elements>
  );
};

export default PaymentForm; 